// src/components/admin/AdminErrors.tsx

import React, { useState } from 'react';
import {
    AlertCircle,
    CheckCircle,
    XCircle,
    Trash2,
    AlertTriangle,
    ChevronDown,
    ChevronUp,
    ExternalLink,
    BookOpen,
    Layers,
    Hash,
    FileQuestion
} from 'lucide-react';
import { ErrorReport } from './types';
import AdminQuestionErrors from './AdminQuestionErrors';
import './AdminErrors.css';

interface AdminErrorsProps {
    errors: ErrorReport[];
    onUpdateStatus: (errorId: string, status: string) => void;
    onDeleteError: (errorId: string) => void;
    onShowToast?: (message: string, type: 'success' | 'error' | 'info') => void;
}

type ErrorTab = 'flashcards' | 'questions';

function stripHtml(html: string): string {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
}

function getStatusIcon(status: string) {
    if (status === 'pending') return <AlertCircle size={16} />;
    if (status === 'accepted') return <CheckCircle size={16} />;
    if (status === 'denied') return <XCircle size={16} />;
    return <AlertCircle size={16} />;
}

function getStatusLabel(status: string) {
    if (status === 'pending') return 'Pendente';
    if (status === 'accepted') return 'Aceito';
    if (status === 'denied') return 'Negado';
    return status;
}

interface FlashcardErrorsProps {
    errors: ErrorReport[];
    onUpdateStatus: (errorId: string, status: string) => void;
    onDeleteError: (errorId: string) => void;
}

const FlashcardErrors: React.FC<FlashcardErrorsProps> = ({ errors, onUpdateStatus, onDeleteError }) => {
    const [expandedCard, setExpandedCard] = useState<string | null>(null);

    if (errors.length === 0) {
        return (
            <div className="adm-err-empty">
                <AlertTriangle size={48} />
                <p>Nenhum erro reportado</p>
            </div>
        );
    }

    return (
        <div className="adm-err-list">
            {errors.map(function (entry) {
                const snap = entry.cardSnapshot;
                const expanded = expandedCard === entry._id;

                return (
                    <div key={entry._id} className={'adm-err-card ' + entry.status}>

                        <div className="adm-err-card-header">
                            <strong>{entry.type}</strong>
                            <span className={'adm-err-status-badge ' + entry.status}>
                                {getStatusIcon(entry.status)}
                                {getStatusLabel(entry.status)}
                            </span>
                        </div>

                        <p className="adm-err-description">{entry.description}</p>

                        {snap && (
                            <div className="adm-err-location">
                                <span className="adm-err-location-item">
                                    <BookOpen size={12} />
                                    {snap.deckName}
                                </span>
                                <span className="adm-err-location-sep">&#8250;</span>
                                <span className="adm-err-location-item">
                                    <Layers size={12} />
                                    {snap.subdeckName}
                                </span>
                                <span className="adm-err-location-sep">&#8250;</span>
                                <span className="adm-err-location-item">
                                    <Hash size={12} />
                                    Card {snap.cardIndex}
                                </span>
                            </div>
                        )}

                        {snap && (snap.cardFront || snap.cardBack) && (
                            <div className="adm-err-card-preview">
                                <button
                                    className="adm-err-btn-expand"
                                    onClick={function () { setExpandedCard(expanded ? null : entry._id); }}
                                >
                                    {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    {expanded ? 'Ocultar card' : 'Ver conteúdo do card'}
                                </button>

                                {expanded && (
                                    <div className="adm-err-card-content">
                                        <div className="adm-err-card-side">
                                            <span className="adm-err-side-label">Frente</span>
                                            <p className="adm-err-side-text">
                                                {stripHtml(snap.cardFront) || '—'}
                                            </p>
                                        </div>
                                        <div className="adm-err-card-side">
                                            <span className="adm-err-side-label">Verso</span>
                                            <p className="adm-err-side-text">
                                                {stripHtml(snap.cardBack) || '—'}
                                            </p>
                                        </div>
                                        {entry.deckId && entry.subdeckId && (
                                            <a
                                                href={'/editor/' + entry.deckId + '/' + entry.subdeckId + (entry.cardId ? '?cardId=' + entry.cardId : '')}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="adm-err-btn-edit"
                                            >
                                                <ExternalLink size={14} />
                                                {entry.cardId ? 'Abrir card no editor' : 'Abrir editor deste subdeck'}
                                            </a>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="adm-err-meta">
                            <span>
                                Reportado por: {entry.userId ? entry.userId.username : 'Usuário Removido'}
                            </span>
                            <span className="adm-err-meta-separator">·</span>
                            <span>
                                {new Date(entry.createdAt).toLocaleDateString('pt-BR')}
                            </span>
                        </div>

                        <div className="adm-err-actions">
                            {entry.status === 'pending' && (
                                <React.Fragment>
                                    <button
                                        className="adm-err-btn-accept"
                                        onClick={function () { onUpdateStatus(entry._id, 'accepted'); }}
                                    >
                                        <CheckCircle size={16} />
                                        Aceitar
                                    </button>
                                    <button
                                        className="adm-err-btn-deny"
                                        onClick={function () { onUpdateStatus(entry._id, 'denied'); }}
                                    >
                                        <XCircle size={16} />
                                        Negar
                                    </button>
                                </React.Fragment>
                            )}
                            {entry.status !== 'pending' && (
                                <span className="adm-err-resolved-label">
                                    Resolvido em {entry.resolvedAt
                                        ? new Date(entry.resolvedAt).toLocaleDateString('pt-BR')
                                        : new Date(entry.createdAt).toLocaleDateString('pt-BR')
                                    }
                                </span>
                            )}
                            <button
                                className="adm-err-btn-delete"
                                onClick={function () { onDeleteError(entry._id); }}
                                title="Excluir"
                            >
                                <Trash2 size={16} />
                            </button>
                        </div>

                    </div>
                );
            })}
        </div>
    );
};

const AdminErrors: React.FC<AdminErrorsProps> = ({ errors, onUpdateStatus, onDeleteError, onShowToast }) => {
    const [activeTab, setActiveTab] = useState<ErrorTab>('flashcards');

    const pendingFlashcards = errors.filter(function (e) { return e.status === 'pending'; }).length;

    const tabs: Array<{ id: ErrorTab; label: string; icon: React.ElementType; badge: number }> = [
        { id: 'flashcards', label: 'Erros Flashcards', icon: Layers, badge: pendingFlashcards },
        { id: 'questions', label: 'Erros Questões', icon: FileQuestion, badge: 0 },
    ];

    return (
        <div className="adm-err-container adm-page">

            <div className="adm-page-header">
                <h2 className="adm-page-title">Erros Reportados</h2>
                <p className="adm-page-subtitle">
                    Gerencie os erros reportados pelos usuários
                </p>
            </div>

            <div className="adm-tabs">
                {tabs.map(function (tab) {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    return (
                        <button
                            key={tab.id}
                            className={`adm-tab${isActive ? ' active' : ''}`}
                            onClick={function () { setActiveTab(tab.id); }}
                        >
                            <Icon size={16} strokeWidth={1.5} />
                            <span>{tab.label}</span>
                            {tab.badge > 0 && (
                                <span className="adm-tab-badge">{tab.badge}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <div className="adm-tab-content">
                {activeTab === 'flashcards' && (
                    <FlashcardErrors
                        errors={errors}
                        onUpdateStatus={onUpdateStatus}
                        onDeleteError={onDeleteError}
                    />
                )}
                {activeTab === 'questions' && (
                    <AdminQuestionErrors
                        onShowToast={onShowToast !== undefined ? onShowToast : function () { return; }}
                    />
                )}
            </div>

        </div>
    );
};

export default AdminErrors;